import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { WattsLogo } from "@/components/WattsLogo";

export const Route = createFileRoute("/reset-password")({
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [validLink, setValidLink] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Supabase fires PASSWORD_RECOVERY when the recovery link is consumed.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setValidLink(true);
        setReady(true);
      }
    });
    // Fallback: also accept an existing session (link already consumed in this tab)
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setValidLink(true);
      setReady(true);
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Mínimo 8 caracteres."); return; }
    if (password !== confirm) { toast.error("Las contraseñas no coinciden."); return; }
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      await supabase.auth.signOut().catch(() => {});
      toast.success("Contraseña actualizada. Inicia sesión.");
      navigate({ to: "/auth", replace: true });
    } catch (err: any) {
      toast.error(err?.message || "No se pudo actualizar la contraseña.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl bg-[#1a1a1a] border border-[#2a2a2a] px-4 text-white placeholder:text-[#666] outline-none transition-colors focus:border-[#F5C518] focus:ring-2 focus:ring-[#F5C518]/30";

  return (
    <div className="flex min-h-screen flex-col bg-[#0f0f0f] fade-in">
      <div className="flex flex-col items-center px-6 pt-8 pb-3 text-center">
        <WattsLogo width={180} />
      </div>
      <div className="flex justify-center"><div className="h-px w-10 bg-[#F5C518]/70" /></div>

      <div className="flex-1 px-6 pt-8 pb-[max(env(safe-area-inset-bottom),1.5rem)]">
        <div className="mx-auto w-full max-w-sm">
          <h1 className="font-display text-2xl text-white mb-2">Nueva contraseña</h1>

          {!ready && <p className="text-sm text-[#888]">Validando enlace…</p>}

          {ready && !validLink && (
            <div className="rounded-xl border border-[#2a2a2a] bg-[#1a1a1a] p-5">
              <p className="text-sm text-white">Este enlace no es válido o ha expirado.</p>
              <button
                onClick={() => navigate({ to: "/forgot-password" })}
                className="press-scale mt-4 w-full rounded-xl bg-[#F5C518] py-3 font-bold text-black"
              >
                Solicitar nuevo enlace
              </button>
            </div>
          )}

          {ready && validLink && (
            <>
              <p className="text-sm text-[#888] mb-6">Elige una nueva contraseña para tu cuenta.</p>
              <form onSubmit={handleSubmit} className="space-y-3">
                <input
                  type="password" autoComplete="new-password" required minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Nueva contraseña"
                  style={{ height: 52 }}
                  className={inputCls}
                />
                <input
                  type="password" autoComplete="new-password" required minLength={8}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Confirmar contraseña"
                  style={{ height: 52 }}
                  className={inputCls}
                />
                <button
                  type="submit"
                  disabled={loading}
                  style={{ height: 52 }}
                  className="press-scale w-full rounded-xl bg-[#F5C518] font-bold text-black disabled:opacity-50"
                >
                  {loading ? "Guardando…" : "Cambiar contraseña"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
